import React from 'react';
import { Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';

interface OAuthCallbackLayoutProps {
  status: 'processing' | 'success' | 'error';
  title: string;
  description: string;
  providerLogo?: React.ReactNode;
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  footerNote?: string;
}

export function OAuthCallbackLayout({
  status,
  title,
  description,
  providerLogo,
  primaryAction,
  secondaryAction,
  footerNote,
}: OAuthCallbackLayoutProps) {
  return (
    <div className="min-h-screen bg-[#FBFBFA] flex flex-col items-center justify-center px-4 py-12">
      <img
        src="/logo_colorato.png"
        alt="Flusia"
        className="h-8 w-auto mb-12 opacity-80"
      />

      <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl p-10 text-center">
        {providerLogo && (
          <div className="flex justify-center mb-6">{providerLogo}</div>
        )}

        <div className="mx-auto mb-6 h-16 w-16 rounded-full flex items-center justify-center"
          style={{
            backgroundColor:
              status === 'processing' ? '#F3F4F6' :
              status === 'success' ? '#F0FDF4' :
              '#FEF2F2',
          }}
        >
          {status === 'processing' && (
            <Loader2 className="h-8 w-8 text-gray-500 animate-spin" strokeWidth={1.5} />
          )}
          {status === 'success' && (
            <CheckCircle className="h-8 w-8 text-green-500" strokeWidth={1.5} />
          )}
          {status === 'error' && (
            <AlertTriangle className="h-8 w-8 text-red-500" strokeWidth={1.5} />
          )}
        </div>

        <h1 className="text-3xl font-semibold text-gray-900 mb-3 tracking-tight">
          {title}
        </h1>

        <p className="text-base text-gray-500 leading-relaxed mb-8">
          {description}
        </p>

        {(primaryAction || secondaryAction) && (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {primaryAction && (
              <Button variant="default" onClick={primaryAction.onClick}>
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button variant="outline" onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}

        {footerNote && (
          <p className="mt-6 text-xs text-gray-400">{footerNote}</p>
        )}
      </div>
    </div>
  );
}
